/**
 * ConfirmDialog — generic confirmation modal for consequential actions.
 *
 * Supports two variants:
 *  - "warning" (default) — amber icon, neutral confirmation
 *  - "danger" — red icon, strong wording
 */
import { AlertTriangle, X } from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  variant?: "warning" | "danger";
  confirmLabel?: string;
  cancelLabel?: string;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  variant = "warning",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
}: Props) {
  if (!open) return null;

  const isDanger = variant === "danger";

  return (
    <div className="confirm-scrim" onClick={onClose}>
      <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
        <button
          className="ev-icon-btn"
          style={{ position: "absolute", top: 12, right: 12 }}
          onClick={onClose}
          aria-label="Close"
        >
          <X size={16} />
        </button>
        <div
          className="confirm-dialog-icon"
          style={{ background: isDanger ? "var(--ev-red-100)" : undefined, color: isDanger ? "var(--ev-red-600)" : undefined }}
        >
          <AlertTriangle size={24} />
        </div>
        <div className="confirm-dialog-title">{title}</div>
        <div className="confirm-dialog-message">{message}</div>
        <div className="confirm-dialog-actions">
          <button className="ev-btn ev-btn-outline" onClick={onClose}>
            {cancelLabel}
          </button>
          <button
            className={`ev-btn ${isDanger ? "ev-btn-danger" : "ev-btn-primary"}`}
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
